import { useEffect, useState } from 'hono/jsx';
import { Layout } from '../components/Layout';
import { Pagination } from '../components/Pagination';
import { PageSpinner } from '../components/Spinner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { navigate, parseQuery, useRoute } from '../lib/router';
import { Site, siteCardStatus } from '../lib/site';

const PER_PAGE = 30;

interface SitesResponse {
  sites: Site[];
  pagination?: { page: number; total: number; totalPages: number };
}

export function SitesList() {
  const { user } = useAuth();
  const path = useRoute();
  const initial = parseQuery(path);
  const [searchInput, setSearchInput] = useState<string>(initial.get('q') || '');
  const [search, setSearch] = useState<string>(initial.get('q') || '');
  const [page, setPage] = useState<number>(parseInt(initial.get('page') || '1', 10) || 1);
  const [sites, setSites] = useState<Site[]>([]);
  const [pagination, setPagination] = useState<SitesResponse['pagination'] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Debounce input → committed search; resets page to 1.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch((s) => s !== searchInput.trim() ? searchInput.trim() : s);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  useEffect(() => { setPage(1); }, [search]);

  // Fetch when search/page change, and reflect them in the URL.
  useEffect(() => {
    const qs = new URLSearchParams();
    if (search) qs.set('q', search);
    if (page > 1) qs.set('page', String(page));
    const url = '/sites' + (qs.toString() ? '?' + qs.toString() : '');
    if (url !== window.location.pathname + window.location.search) {
      window.history.replaceState({}, '', url);
    }
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const p = new URLSearchParams({ page: String(page), limit: String(PER_PAGE) });
        if (search) p.set('search', search);
        const r = await api('/sites?' + p.toString());
        if (!r.ok) throw new Error('Failed to load sites');
        const data: SitesResponse = await r.json();
        if (cancelled) return;
        setSites(data.sites || []);
        setPagination(data.pagination || null);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load sites');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [search, page]);

  const firstName = (user?.name && user.name.split(' ')[0]) || 'there';

  return (
    <Layout active="sites">
      <div class="mb-8">
        <h1 class="text-2xl font-bold text-slate-100">Welcome back, {firstName}!</h1>
        <p class="text-slate-400 mt-1">Manage your websites from one place</p>
      </div>
      <div class="bg-dark-800 rounded-xl border border-slate-700/50 shadow-sm overflow-hidden">
        <div class="px-6 py-4 border-b border-slate-700/50">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h2 class="text-lg font-semibold text-slate-100">Your Sites</h2>
            <div class="flex items-center gap-3">
              <div class="relative">
                <svg class="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                <input
                  type="text"
                  value={searchInput}
                  onInput={(e: Event) => setSearchInput((e.currentTarget as HTMLInputElement).value)}
                  placeholder="Search sites..."
                  class="w-full sm:w-64 pl-9 pr-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50"
                />
              </div>
              <a
                href="/sites/new"
                onClick={(e: MouseEvent) => { e.preventDefault(); navigate('/sites/new'); }}
                class="inline-flex items-center gap-2 bg-cyan-500 text-dark-900 px-4 py-2 rounded-lg text-sm font-medium hover:bg-cyan-400 transition-colors whitespace-nowrap"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                </svg>
                Create Site
              </a>
            </div>
          </div>
        </div>
        <div class="p-6">
          {loading ? (
            <PageSpinner label="Loading your sites…" />
          ) : error ? (
            <ErrorState />
          ) : sites.length === 0 ? (
            <EmptyState searching={!!search} />
          ) : (
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sites.map((s) => <SiteCard key={s.id} site={s} />)}
            </div>
          )}
        </div>
        {pagination && pagination.totalPages > 1 && (
          <Pagination pagination={pagination} perPage={PER_PAGE} onGo={setPage} />
        )}
      </div>
    </Layout>
  );
}

function SiteCard({ site }: { site: Site }) {
  const isBuilding = site.status === 'building';
  const status = siteCardStatus(site);
  // Show the custom domain when configured. (The default subdomain is implicit
  // from the site name + slug, so we keep that case quiet.)
  const customDomain = site.domain || null;
  const hasFooter = !!status || !!customDomain;
  return (
    <div class="site-card border border-slate-700/50 rounded-lg p-4 hover:border-slate-600 hover:shadow-sm transition-all bg-dark-700">
      <div class="flex items-center justify-between gap-3">
        <a
          href={`/sites/${site.id}`}
          onClick={(e: MouseEvent) => { e.preventDefault(); navigate(`/sites/${site.id}`); }}
          class="flex-1 min-w-0 hover:opacity-80 transition-opacity"
        >
          <h3 class="font-medium text-slate-100 truncate">{site.name}</h3>
        </a>
        <a
          href={`/sites/${site.id}`}
          onClick={(e: MouseEvent) => { e.preventDefault(); navigate(`/sites/${site.id}`); }}
          class="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-dark-700 border border-slate-600/50 text-slate-200 rounded-lg text-sm font-medium hover:bg-dark-600 hover:border-slate-500 hover:text-slate-100 transition-colors"
        >
          <svg class="w-3.5 h-3.5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
          </svg>
          Manage
        </a>
      </div>
      {isBuilding && (
        <div class="mt-3 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <span class="text-xs text-amber-400">Building…</span>
        </div>
      )}
      {hasFooter && (
        <div class="mt-3 pt-3 border-t border-slate-700/30 flex items-center justify-between gap-2 px-1">
          {status ? (
            <div class="flex items-center gap-2 min-w-0">
              <span class={`w-2 h-2 rounded-full shrink-0 ${status.dot}`}></span>
              <span class="text-xs text-slate-400 truncate">{status.label}</span>
            </div>
          ) : <span />}
          {customDomain && (
            <a
              href={`https://${customDomain}`}
              target="_blank"
              rel="noopener"
              onClick={(e: MouseEvent) => e.stopPropagation()}
              title={`Open ${customDomain}`}
              class="text-xs text-slate-500 font-mono truncate hover:text-cyan-400 transition-colors"
            >{customDomain}</a>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ searching }: { searching: boolean }) {
  return (
    <div class="text-center py-12">
      <div class="w-12 h-12 rounded-full bg-dark-700 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {searching
            ? <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            : <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3"/>
          }
        </svg>
      </div>
      <h3 class="text-slate-200 font-medium">{searching ? 'No sites found' : 'No sites yet'}</h3>
      <p class="text-slate-500 text-sm mt-1 mb-4">{searching ? 'Try a different search term' : 'Create your first website to get started'}</p>
      {!searching && (
        <a
          href="/sites/new"
          onClick={(e: MouseEvent) => { e.preventDefault(); navigate('/sites/new'); }}
          class="inline-flex items-center gap-2 bg-cyan-500 text-dark-900 px-4 py-2 rounded-lg text-sm font-medium hover:bg-cyan-400 transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          Create Site
        </a>
      )}
    </div>
  );
}

function ErrorState() {
  return (
    <div class="text-center py-12">
      <div class="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
        </svg>
      </div>
      <h3 class="text-slate-200 font-medium">Could not load sites</h3>
      <p class="text-slate-500 text-sm mt-1">Please try refreshing the page</p>
    </div>
  );
}
